import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const COMPANY_SIZE_OPTIONS = [
  '1-5 employees',
  '6-50 employees',
  '51-250 employees',
  '250+ employees'
];

function AccessRequest({ branding }) {
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [message, setMessage] = useState('');
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const response = await axios.get('/api/sectors');
      setAvailableSectors(response.data);
    } catch (err) {
      console.error('Failed to load sectors:', err);
      setError('Failed to load industry sectors. Please try again later.');
    }
  };

  const handleSectorToggle = (sector) => {
    setSelectedSectors(prev => 
      prev.includes(sector) 
        ? prev.filter(s => s !== sector)
        : [...prev, sector]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (selectedSectors.length === 0) {
      setError('Please select at least one industry sector');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/access-request', {
        companyName,
        contactName,
        email,
        phone,
        sectors: selectedSectors,
        companySize,
        message
      });

      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1 className="auth-title">Request Received!</h1>
            <p className="auth-subtitle">Thank you for your interest in our health & safety platform</p>
          </div>

          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <p style={{ marginBottom: '1rem', lineHeight: '1.6', color: '#333' }}>
              We've received your access request and will be in touch shortly to discuss your requirements and arrange access to the platform.
            </p>
            <p style={{ lineHeight: '1.6', color: '#666' }}>
              A member of our team will contact you within 1-2 business days.
            </p>
          </div>

          <div className="auth-footer">
            <Link to="/login" className="auth-link">Return to login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">Request Access</h1>
          <p className="auth-subtitle">Get in touch to access industry-specific health & safety documentation</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="companyName" className="form-label">Company Name *</label>
            <input
              type="text"
              id="companyName"
              className="form-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              placeholder="Your company name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="contactName" className="form-label">Contact Name *</label>
            <input
              type="text"
              id="contactName"
              className="form-input"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              required
              placeholder="Your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">Email Address *</label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your.email@company.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone" className="form-label">Phone Number</label>
            <input
              type="tel"
              id="phone"
              className="form-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="form-group">
            <label htmlFor="companySize" className="form-label">Company Size</label>
            <select
              id="companySize"
              className="form-input"
              value={companySize}
              onChange={(e) => setCompanySize(e.target.value)}
            >
              <option value="">Select company size (optional)</option>
              {COMPANY_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Select Your Industry Sectors *</label>
            {availableSectors.length === 0 ? (
              <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', color: '#666' }}>
                Loading sectors...
              </div>
            ) : (
              <div className="sector-checkboxes">
                {availableSectors.map(sector => (
                  <div key={sector.id} className="sector-checkbox">
                    <input
                      type="checkbox"
                      id={`sector-${sector.id}`}
                      checked={selectedSectors.includes(sector.name)}
                      onChange={() => handleSectorToggle(sector.name)}
                    />
                    <label htmlFor={`sector-${sector.id}`}>{sector.name}</label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">Additional Information</label>
            <textarea
              id="message"
              className="form-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="3"
              placeholder="Tell us about your requirements (optional)"
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading || availableSectors.length === 0}>
            {loading ? 'Submitting...' : 'Request Access'}
          </button>
        </form>

        <div className="auth-footer">
          Already have an account? <Link to="/login" className="auth-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

export default AccessRequest;
